import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { apiClient } from '../config/apiConfig';
import socketService from '../services/socketService';
import { getLogger } from '../components/features/Campaigns/dashboard/config/environmentConfig';
import { useAuth } from './auth/AuthContext';

const logger = getLogger('log');

const CreatorCacheContext = createContext();
const CREATOR_PAGE_SIZE = 100;
const EMPTY_CREATOR_CACHE = new Map();

/**
 * Creator Cache Provider Component
 *
 * Provides global in-memory cache for all creators with real-time WebSocket updates.
 * Follows the WorkloadContext pattern for consistency and performance.
 *
 * Features:
 * - Loads creators in bounded pages on demand
 * - Map-based storage for O(1) lookups
 * - WebSocket integration for real-time updates
 * - LIST projection for optimal performance (~80% size reduction)
 * - Session-persistent cache
 * - Automatic CRUD updates
 */
export const CreatorCacheProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const authIdentity = isAuthenticated
    ? String(user?._id || user?.id || user?.username || '')
    : '';
  // State management
  const [creatorCache, setCreatorCache] = useState(new Map());
  const [cacheOwnerIdentity, setCacheOwnerIdentity] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [totalCreators, setTotalCreators] = useState(0);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreCreators, setHasMoreCreators] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestGenerationRef = useRef(0);
  const initialLoadControllerRef = useRef(null);
  const moreLoadGenerationRef = useRef(null);

  const ownsCurrentCache = Boolean(authIdentity) && cacheOwnerIdentity === authIdentity;
  const visibleCreatorCache = ownsCurrentCache ? creatorCache : EMPTY_CREATOR_CACHE;
  // Ref is updated during render so callbacks cannot observe the prior account before effects run.
  const creatorCacheRef = useRef(visibleCreatorCache);
  creatorCacheRef.current = visibleCreatorCache;
  const authIdentityRef = useRef(authIdentity);
  authIdentityRef.current = authIdentity;
  const cacheOwnerIdentityRef = useRef(cacheOwnerIdentity);
  cacheOwnerIdentityRef.current = cacheOwnerIdentity;

  /**
   * Load all creators with LIST projection
   * Uses LIST projection for optimized data transfer (~80% size reduction)
   */
  const loadInitialCreators = useCallback(async (requestGeneration = requestGenerationRef.current) => {
    if (authLoading || !isAuthenticated || !authIdentity || requestGeneration !== requestGenerationRef.current) {
      return 0;
    }

    initialLoadControllerRef.current?.abort();
    const controller = new AbortController();
    initialLoadControllerRef.current = controller;
    try {
      setCacheOwnerIdentity(authIdentity);
      setLoading(true);
      setError(null);
      logger('Loading all creators with LIST projection...');

      const response = await apiClient.get('/creators', {
        signal: controller.signal,
        params: {
          page: 1,
          limit: CREATOR_PAGE_SIZE,
          sort: 'createdAt',
          order: 'desc',
          projection: 'LIST' // Use LIST projection for table views
        }
      });

      if (requestGeneration !== requestGenerationRef.current || controller.signal.aborted) return 0;

      const creators = response.data.creators || response.data || [];
      const pagination = response.data.pagination || {};
      const newCache = new Map();

      creators.forEach(creator => {
        if (creator._id) {
          newCache.set(creator._id, creator);
        }
      });

      logger(`Loaded ${newCache.size} creators with LIST projection`);
      setCacheOwnerIdentity(authIdentity);
      setCreatorCache(newCache);
      const total = pagination.total ?? response.data.total ?? creators.length;
      setLoadProgress({ loaded: newCache.size, total });
      setTotalCreators(total);
      setCurrentPage(pagination.page || 1);
      setHasMoreCreators(
        pagination.pages
          ? (pagination.page || 1) < pagination.pages
          : newCache.size < total
      );

      // Mark as initialized after loading
      setIsInitialized(true);

      return newCache.size;
    } catch (err) {
      if (requestGeneration !== requestGenerationRef.current || controller.signal.aborted || err.name === 'CanceledError' || err.name === 'AbortError') {
        return 0;
      }
      logger('Error loading initial creators:', err);
      setError('Failed to load creators');
      throw err;
    } finally {
      if (requestGeneration === requestGenerationRef.current) setLoading(false);
      if (initialLoadControllerRef.current === controller) initialLoadControllerRef.current = null;
    }
  }, [authIdentity, authLoading, isAuthenticated]);

  const loadMoreCreators = useCallback(async () => {
    if (authLoading || !isAuthenticated || !authIdentity || loadingMore || !hasMoreCreators) return;

    const requestGeneration = requestGenerationRef.current;
    // State updates are asynchronous; fence duplicate calls in the same render/StrictMode effect.
    if (moreLoadGenerationRef.current === requestGeneration) return;
    moreLoadGenerationRef.current = requestGeneration;

    try {
      setLoadingMore(true);
      setError(null);
      const nextPage = currentPage + 1;
      logger(`Loading creator page ${nextPage} with LIST projection...`);

      const response = await apiClient.get('/creators', {
        params: {
          page: nextPage,
          limit: CREATOR_PAGE_SIZE,
          sort: 'createdAt',
          order: 'desc',
          projection: 'LIST' // Use LIST projection for table views
        }
      });

      if (requestGeneration !== requestGenerationRef.current) return;

      const creators = response.data.creators || response.data || [];
      const pagination = response.data.pagination || {};

      if (creators.length === 0) {
        setHasMoreCreators(false);
        return;
      }

      setCreatorCache(prev => {
        if (cacheOwnerIdentityRef.current !== authIdentityRef.current) return prev;
        const newCache = new Map(prev);
        creators.forEach(creator => {
          if (creator._id) {
            newCache.set(creator._id, creator);
          }
        });

        const total = pagination.total ?? totalCreators;
        logger(`Total creators in cache: ${newCache.size} (using LIST projection)`);
        setLoadProgress({ loaded: newCache.size, total });
        setTotalCreators(total);
        return newCache;
      });
      setCurrentPage(pagination.page || nextPage);
      setHasMoreCreators(
        pagination.pages
          ? (pagination.page || nextPage) < pagination.pages
          : creators.length === CREATOR_PAGE_SIZE
      );
    } catch (err) {
      if (requestGeneration !== requestGenerationRef.current) return;
      logger('Error loading more creators:', err);
      setError('Failed to load more creators');
    } finally {
      if (moreLoadGenerationRef.current === requestGeneration) moreLoadGenerationRef.current = null;
      if (requestGeneration === requestGenerationRef.current) setLoadingMore(false);
    }
  }, [authIdentity, authLoading, currentPage, hasMoreCreators, isAuthenticated, loadingMore, totalCreators]);

  /**
   * Get creator from cache by ID
   */
  const getCreator = useCallback((creatorId) => {
    if (!creatorId) return null;
    return creatorCacheRef.current.get(creatorId) || null;
  }, []);

  /**
   * Get multiple creators by IDs
   */
  const getCreators = useCallback((creatorIds) => {
    if (!Array.isArray(creatorIds)) return [];
    return creatorIds
      .map(id => visibleCreatorCache.get(id))
      .filter(Boolean);
  }, [visibleCreatorCache]);

  /**
   * Get all creators as array
   */
  const getAllCreators = useCallback(() => {
    return Array.from(visibleCreatorCache.values());
  }, [visibleCreatorCache]);

  /**
   * Search creators by criteria
   */
  const searchCreators = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return getAllCreators();
    }

    const term = searchTerm.toLowerCase().trim();
    const allCreators = getAllCreators();

    return allCreators.filter(creator => {
      const twitterUsername = creator.twitter?.username?.toLowerCase() || '';
      const discordUsername = creator.discord?.username?.toLowerCase() || '';
      const telegramUsername = creator.telegram?.username?.toLowerCase() || '';
      const walletAddress = creator.wallet?.address?.toLowerCase() || '';
      const country = creator.location?.country?.toLowerCase() || '';
      const region = creator.location?.region?.toLowerCase() || '';

      return (
        (creator.notes || '').toLowerCase().includes(term) ||
        (creator.name || '').toLowerCase().includes(term) ||
        twitterUsername.includes(term) ||
        discordUsername.includes(term) ||
        telegramUsername.includes(term) ||
        walletAddress.includes(term) ||
        country.includes(term) ||
        region.includes(term)
      );
    });
  }, [getAllCreators]);

  /**
   * Filter creators by status
   */
  const filterCreatorsByStatus = useCallback((status) => {
    if (!status || status === 'all') {
      return getAllCreators();
    }

    return getAllCreators().filter(creator => creator.status === status);
  }, [getAllCreators]);

  /**
   * Filter creators by type
   */
  const filterCreatorsByType = useCallback((type) => {
    if (!type || type === '__all__' || type === 'all') return getAllCreators();
    return getAllCreators().filter(c => c.creatorType?.includes(type));
  }, [getAllCreators]);

  /**
   * Update creator in cache
   */
  const updateCreator = useCallback((creatorId, updates) => {
    if (!creatorId) return;

    setCreatorCache(prev => {
      if (cacheOwnerIdentityRef.current !== authIdentityRef.current) return prev;
      const newCache = new Map(prev);
      const existing = newCache.get(creatorId);

      if (existing) {
        // Deep merge for nested objects
        newCache.set(creatorId, {
          ...existing,
          ...updates,
          wallet: {
            ...(existing.wallet || {}),
            ...(updates.wallet || {})
          },
          twitter: {
            ...(existing.twitter || {}),
            ...(updates.twitter || {})
          },
          discord: {
            ...(existing.discord || {}),
            ...(updates.discord || {})
          },
          location: {
            ...(existing.location || {}),
            ...(updates.location || {})
          }
        });
      }

      return newCache;
    });
  }, []);

  /**
   * Add new creator to cache
   */
  const addCreator = useCallback((creator) => {
    if (!creator || !creator._id) return;

    setCreatorCache(prev => {
      if (cacheOwnerIdentityRef.current !== authIdentityRef.current) return prev;
      const newCache = new Map(prev);
      newCache.set(creator._id, creator);
      setTotalCreators(prev => prev + 1);
      setLoadProgress(prev => ({ ...prev, loaded: prev.loaded + 1, total: prev.total + 1 }));
      return newCache;
    });
  }, []);

  /**
   * Remove creator from cache
   */
  const removeCreator = useCallback((creatorId) => {
    if (!creatorId) return;

    setCreatorCache(prev => {
      if (cacheOwnerIdentityRef.current !== authIdentityRef.current) return prev;
      const newCache = new Map(prev);
      newCache.delete(creatorId);
      setTotalCreators(prev => Math.max(0, prev - 1));
      setLoadProgress(prev => ({ ...prev, loaded: Math.max(0, prev.loaded - 1), total: Math.max(0, prev.total - 1) }));
      return newCache;
    });
  }, []);

  /**
   * Refresh cache (force reload)
   */
  const refreshCache = useCallback(async () => {
    const requestGeneration = ++requestGenerationRef.current;
    initialLoadControllerRef.current?.abort();
    initialLoadControllerRef.current = null;
    setCreatorCache(new Map());
    setCacheOwnerIdentity('');
    setIsInitialized(false);
    setCurrentPage(0);
    setHasMoreCreators(false);
    setLoadingMore(false);
    await loadInitialCreators(requestGeneration);
  }, [loadInitialCreators]);

  /**
   * Fetch and update a single creator from API
   * More efficient than refreshCache() when only one creator needs updating
   */
  const fetchAndUpdateCreator = useCallback(async (creatorId) => {
    if (authLoading || !isAuthenticated || !authIdentity) return null;
    const requestGeneration = requestGenerationRef.current;
    try {
      logger(`Fetching single creator: ${creatorId}`);
      const response = await apiClient.get(`/creators/${creatorId}?projection=FULL`);

      if (response.data && requestGeneration === requestGenerationRef.current) {
        // Direct state update to avoid stale closure issues
        setCreatorCache(prev => {
          if (cacheOwnerIdentityRef.current !== authIdentityRef.current) return prev;
          const newCache = new Map(prev);
          newCache.set(creatorId, response.data);
          logger(`Updated creator ${creatorId} in cache (${newCache.size} total creators)`);
          return newCache;
        });
        return response.data;
      }
    } catch (error) {
      if (requestGeneration !== requestGenerationRef.current) return null;
      logger(`Error fetching creator ${creatorId}:`, error);
      throw error;
    }
  }, [authIdentity, authLoading, isAuthenticated]);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    requestGenerationRef.current += 1;
    initialLoadControllerRef.current?.abort();
    initialLoadControllerRef.current = null;
    setCreatorCache(new Map());
    setCacheOwnerIdentity('');
    setIsInitialized(false);
    setTotalCreators(0);
    setLoadProgress({ loaded: 0, total: 0 });
    setCurrentPage(0);
    setHasMoreCreators(false);
    setLoading(false);
    setLoadingMore(false);
    setError(null);
  }, []);

  /**
   * Set up WebSocket listeners for real-time updates
   */
  useEffect(() => {
    if (!isAuthenticated || !authIdentity || !isInitialized) return;

    logger('Setting up WebSocket listeners for creator cache');

    // Creator updated
    const unsubscribeUpdate = socketService.on('creator:updated', (data) => {
      logger('Creator updated via WebSocket:', data);
      const creator = data.creator || data;
      if (creator && creator._id) {
        updateCreator(creator._id, creator);
      }
    });

    // Creator created
    const unsubscribeCreate = socketService.on('creator:created', (data) => {
      logger('Creator created via WebSocket:', data);
      const creator = data.creator || data;
      if (creator && creator._id) {
        addCreator(creator);
      }
    });

    // Creator deleted
    const unsubscribeDelete = socketService.on('creator:deleted', (data) => {
      logger('Creator deleted via WebSocket:', data);
      const creatorId = data.creatorId || data._id;
      if (creatorId) {
        removeCreator(creatorId);
      }
    });

    // Campaign creator status updates (affects creator data)
    const unsubscribeStatus = socketService.on('creator:status:changed', (data) => {
      logger('Creator status changed via WebSocket:', data);
      if (data.creatorId) {
        // Use the ref to check existence — no need to add getCreator to deps
        const existing = creatorCacheRef.current.get(data.creatorId);
        if (existing) {
          updateCreator(data.creatorId, { status: data.status });
        }
      }
    });

    // Cleanup
    return () => {
      unsubscribeUpdate();
      unsubscribeCreate();
      unsubscribeDelete();
      unsubscribeStatus();
    };
  }, [authIdentity, isAuthenticated, isInitialized, updateCreator, addCreator, removeCreator]);

  /** Load and isolate the cache for the currently authenticated account. */
  useEffect(() => {
    const requestGeneration = ++requestGenerationRef.current;
    initialLoadControllerRef.current?.abort();
    initialLoadControllerRef.current = null;
    setCreatorCache(new Map());
    setCacheOwnerIdentity('');
    setIsInitialized(false);
    setTotalCreators(0);
    setLoadProgress({ loaded: 0, total: 0 });
    setCurrentPage(0);
    setHasMoreCreators(false);
    setLoadingMore(false);
    setError(null);

    if (authLoading || !isAuthenticated || !authIdentity) {
      setLoading(false);
      return undefined;
    }

    loadInitialCreators(requestGeneration).catch(() => {});
    return () => {
      if (requestGeneration === requestGenerationRef.current) {
        initialLoadControllerRef.current?.abort();
      }
    };
  }, [authIdentity, authLoading, isAuthenticated, loadInitialCreators]);

  // Memoized context value
  const value = useMemo(() => ({
    // State
    loading: Boolean(authIdentity) && loading,
    isInitialized: ownsCurrentCache && isInitialized,
    error: ownsCurrentCache ? error : null,
    totalCreators: ownsCurrentCache ? totalCreators : 0,
    loadProgress: ownsCurrentCache ? loadProgress : { loaded: 0, total: 0 },
    hasMoreCreators: ownsCurrentCache ? hasMoreCreators : false,
    loadingMore: ownsCurrentCache && loadingMore,
    cacheSize: visibleCreatorCache.size,

    // Getters
    getCreator,
    getCreators,
    getAllCreators,
    searchCreators,
    filterCreatorsByStatus,
    filterCreatorsByType,

    // Mutations
    updateCreator,
    addCreator,
    removeCreator,

    // Cache management
    refreshCache,
    loadMoreCreators,
    fetchAndUpdateCreator,
    clearCache
  }), [
    authIdentity,
    loading,
    isInitialized,
    error,
    totalCreators,
    loadProgress,
    hasMoreCreators,
    loadingMore,
    ownsCurrentCache,
    visibleCreatorCache.size,
    getCreator,
    getCreators,
    getAllCreators,
    searchCreators,
    filterCreatorsByStatus,
    filterCreatorsByType,
    updateCreator,
    addCreator,
    removeCreator,
    refreshCache,
    loadMoreCreators,
    fetchAndUpdateCreator,
    clearCache
  ]);

  return (
    <CreatorCacheContext.Provider value={value}>
      {children}
    </CreatorCacheContext.Provider>
  );
};

/**
 * Custom hook for accessing Creator Cache Context
 */
export const useCreatorCache = () => {
  const context = useContext(CreatorCacheContext);

  if (!context) {
    throw new Error('useCreatorCache must be used within a CreatorCacheProvider');
  }

  return context;
};

export default CreatorCacheContext;
